import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, MapPin, Globe, Linkedin, Twitter, Facebook, Youtube, Instagram, Phone } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { HID } from "@/constants/cosecIds";

const LOGO_WHITE = "/assets/cosec-logo-white.png";

export default function Footer() {
  return (
    <>
      {/* CTA banner, light surface */}
      <section className="bg-white border-b border-border">
        <div className="container-cv py-20">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-accent">
                <span className="h-px w-8 bg-accent" /> Let&apos;s talk
              </span>
              <h2 className="mt-4 font-display text-4xl md:text-6xl font-bold tracking-tight leading-[1.02] text-primary">
                Ready to take control of{" "}
                <span className="text-accent">company compliance?</span>
              </h2>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <a
                href="/contact"
                data-testid={HID.footerContact}
                className="group inline-flex items-center gap-3 rounded-full bg-accent text-primary font-bold px-7 py-4 text-base hover:bg-accent/90 transition-colors"
              >
                Book a demo
                <ArrowUpRight className="h-5 w-5 transition-transform group-hover:rotate-45" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="relative bg-primary text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-[0.06] pointer-events-none" />
        <div className="absolute -top-40 right-0 h-96 w-96 rounded-full bg-accent/10 blur-3xl pointer-events-none" />

        <div className="container-cv relative pt-20 pb-12">
          <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Link to="/" data-testid={HID.footerLogo}>
              <img src={LOGO_WHITE} alt="CoSec Global" className="h-10 w-auto" />
            </Link>
            <p className="mt-5 text-sm text-white/70 max-w-sm leading-relaxed">
              An AI-powered cloud-based entity management platform that simplifies
              corporate governance, compliance and company secretarial management for
              growing businesses worldwide.
            </p>

            <div className="mt-6 space-y-2 text-sm text-white/70">
              <a href="https://www.cosecglobal.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:text-accent transition-colors">
                <Globe className="h-4 w-4" /> www.cosecglobal.com
              </a>
              <a href="mailto:contact@cosecglobal.com" className="flex items-center gap-2 hover:text-accent transition-colors" data-testid="footer-email">
                <Mail className="h-4 w-4" /> contact@cosecglobal.com
              </a>
              <a href="tel:+443301332696" className="flex items-center gap-2 hover:text-accent transition-colors" data-testid="footer-phone">
                <Phone className="h-4 w-4" /> +44 330 133 2696
              </a>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4" /> United Kingdom &middot; India
              </div>
            </div>

            <div className="mt-6 flex items-center gap-3" data-testid="footer-social">
              <a href="https://www.linkedin.com/company/cosecglobal" target="_blank" rel="noreferrer" aria-label="LinkedIn" data-testid="social-linkedin" className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-accent hover:text-primary transition-colors">
                <Linkedin className="h-4 w-4" />
              </a>
              <a href="https://x.com/cosecglobal" target="_blank" rel="noreferrer" aria-label="X (Twitter)" data-testid="social-x" className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-accent hover:text-primary transition-colors">
                <Twitter className="h-4 w-4" />
              </a>
              <a href="https://www.facebook.com/cosecglobal" target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="social-facebook" className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-accent hover:text-primary transition-colors">
                <Facebook className="h-4 w-4" />
              </a>
              <a href="https://www.youtube.com/@cosecglobal" target="_blank" rel="noreferrer" aria-label="YouTube" data-testid="social-youtube" className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-accent hover:text-primary transition-colors">
                <Youtube className="h-4 w-4" />
              </a>
              <a href="https://www.instagram.com/cosecglobal" target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="social-instagram" className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-accent hover:text-primary transition-colors">
                <Instagram className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs font-bold uppercase tracking-wider text-white/50">Platform</div>
            <ul className="mt-4 space-y-2 text-sm text-white/80">
              <li><Link to="/cosecapp" className="hover:text-accent">Features</Link></li>
              <li><Link to="/services" className="hover:text-accent">Services</Link></li>
              <li><Link to="/pricing" className="hover:text-accent">Pricing</Link></li>
              <li><Link to="/about" className="hover:text-accent">About</Link></li>
              <li><Link to="/contact" className="hover:text-accent">Contact</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs font-bold uppercase tracking-wider text-white/50">Products</div>
            <ul className="mt-4 space-y-2 text-sm text-white/80">
              <li><Link to="/cosecapp" className="hover:text-accent">CoSec App</Link></li>
              <li>
                <Link to="/coverify" data-testid={HID.footerCoverifyLink} className="hover:text-accent inline-flex items-center gap-1.5">
                  CoVerify
                  <span className="text-[9px] font-bold uppercase tracking-wider text-accent">Live</span>
                </Link>
              </li>
              <li>
                <Link to="/cosearch" className="hover:text-accent inline-flex items-center gap-1.5">
                  CoSearch
                  <span className="text-[9px] font-bold uppercase tracking-wider text-accent">New</span>
                </Link>
              </li>
              <li><Link to="/coform" className="hover:text-accent">CoForm</Link></li>
              <li><Link to="/company-secretaries" className="hover:text-accent">For Co Secs</Link></li>
              <li><Link to="/accountants" className="hover:text-accent">For Accountants</Link></li>
              <li><Link to="/corporate-law" className="hover:text-accent">For Legal</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs font-bold uppercase tracking-wider text-white/50">Resources</div>
            <ul className="mt-4 space-y-2 text-sm text-white/80">
              <li><Link to="/blog" className="hover:text-accent">Blog</Link></li>
              <li><Link to="/case-studies" className="hover:text-accent">Case Studies</Link></li>
              <li><Link to="/guides" className="hover:text-accent">Guides</Link></li>
              <li><Link to="/glossary" className="hover:text-accent">Glossary</Link></li>
              <li><Link to="/faqs" className="hover:text-accent">FAQs</Link></li>
              <li><Link to="/partners" className="hover:text-accent">Partners</Link></li>
              <li><Link to="/about" className="hover:text-accent">About CoSec Global</Link></li>
              <li><Link to="/contact" className="hover:text-accent">Contact</Link></li>
            </ul>
          </div>
        </div>

        {/* Trust strip, unified visual treatment, globally-relevant standards only */}
        <div className="mt-14 pt-10 border-t border-white/10">
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-white/50 text-center">
            Certified, compliant &amp; trusted
          </div>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="trust-strip">
            {[
              { name: "ISO 9001", sub: "Quality", icon: "Award" },
              { name: "ISO 27001", sub: "Info Security", icon: "ShieldCheck" },
              { name: "ISO 27701", sub: "Privacy", icon: "Lock" },
              { name: "GDPR", sub: "Compliant", icon: "BadgeCheck" },
              { name: "AWS Cloud", sub: "Hosted", icon: "Cloud" },
              { name: "Companies House", sub: "UK Authorised", icon: "Landmark" },
            ].map((b) => {
              const Ic = LucideIcons[b.icon] || LucideIcons.Award;
              return (
                <div
                  key={b.name}
                  data-testid={`trust-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-sm h-20 px-3 flex items-center gap-3 hover:bg-white/[0.08] hover:border-accent/40 transition-colors"
                >
                  <div className="h-10 w-10 rounded-lg bg-accent/15 text-accent grid place-items-center shrink-0">
                    <Ic className="h-5 w-5" strokeWidth={2} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-sm font-bold text-white truncate">{b.name}</div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-white/60">{b.sub}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs text-white/50">
            © {new Date().getFullYear()} CoSec Global. All rights reserved.
          </div>
          <div className="flex items-center gap-5 text-xs text-white/60">
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-accent transition-colors">Privacy</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-accent transition-colors">Terms</Link>
            <Link to="/cookies" data-testid="footer-cookies" className="hover:text-accent transition-colors">Cookies</Link>
            <button
              data-testid="footer-cookie-settings"
              onClick={() => window.dispatchEvent(new CustomEvent("cosec-open-cookie-prefs"))}
              className="hover:text-accent transition-colors"
            >
              Cookie settings
            </button>
          </div>
          <div className="text-xs text-white/40 inline-flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            Global compliance &middot; UK &middot; India
          </div>
        </div>
      </div>
      </footer>
    </>
  );
}
